import { useContext, useEffect, useState } from "react";
import { App as Capacitor } from "@capacitor/app";
import useToast from "./useToast";
import { NavContext } from "@ionic/react";
import { ROUTES } from "@/utils/routes";

/**
 * Custom hook for handling Android back button behavior in Capacitor apps.
 *
 * This hook implements a double-tap-to-exit pattern when the user is on the root route.
 * On the first back press, it shows a toast message asking the user to press back again to exit.
 * On the second back press within 2 seconds, it exits the app.
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   useBackButton();
 *   return <div>My component content</div>;
 * };
 * ```
 */
const useBackButton = () => {
  const { routeInfo, goBack } = useContext(NavContext);
  const [backPressCount, setBackPressCount] = useState(0);
  const displayToast = useToast();

  useEffect(() => {
    const onBack = () => {
      if (
        routeInfo?.pathname === ROUTES.ROOT
      ) {
        if (backPressCount === 0) {
          displayToast({
            message: "Press back again to exit.",
            duration: 2000,
            position: "bottom",
          });
          setBackPressCount(1);

          setTimeout(() => {
            setBackPressCount(0);
          }, 2000);
        } else if (backPressCount === 1) {
          Capacitor.exitApp();
        }
      }
    };

    Capacitor.addListener("backButton", onBack);

    return () => {
      Capacitor.removeAllListeners();
    };
  }, [backPressCount, routeInfo, goBack]);

  return null;
};

export default useBackButton;
